import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaxResidencyCountryInformation, TaxResidencyInformation } from "../../model/TaxResidencyType";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import {
    CreateZahtjevPoreznaRezidentnostRequest,
    DrzavaOporezivanja,
    useUpdateTaxResidency
} from "../../api/RegistrationApi";
import { useNotification } from "../../common/context/NotificationProvider";
import { Formik, FormikErrors, FormikValues } from "formik";
import { Button } from "../common/Button";
import Switch from "../common/Switch";
import {
    isDrzaveOporezivanjaValid,
    validateField,
    validateForm
} from "./texResidencyInformationValidation";
import { ValidateOnMount } from "../common/ValidateOnMount";
import { Card } from "../common/Card";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { TAX_RESIDENCY_INFORMATION_STATUS } from "../../common/constants/navigationConstants";
import { Grid } from "@mui/material";
import { Country } from "../../model/PersonalInformationType";
import { TaxResidencyCountryInput } from "./TaxResidencyCountryInput";
import { useMediaQuery } from "react-responsive";
import InputField from "../common/InputField";
import { toNullOrString } from "../../util/stringUtil";
import { SAD_OZNAKA } from "../../common/data/sifarnik";
import AddIcon from "@mui/icons-material/Add";
import { Tooltip } from "../common/Tooltip";
import { useTranslation } from "react-i18next";
import { scrollToFirstError } from "../../util/formUtil";

const DRZAVE_OPOREZIVANJA_MAX_LENGTH = 5;

const EMPTY_DRZAVA_OPOREZIVANJA: TaxResidencyCountryInformation = {
    country: {} as Country,
    taxNumber: "",
}

const INITIAL_TAX_RESIDENCY_INFORMATION_FORMIK_VALUES: TaxResidencyInformation = {
    imaDodatnoAmerickoDrzavljanstvo: false,
    porezniObveznikSAD: false,
    zelenaKarta: false,
    posjedujePorezniBrojSAD: false,
    porezniBrojSAD: "",
    porezniObveznikDrzaveKojaNijeSAD: false,
    drzaveOporezivanja: [],
} as TaxResidencyInformation;

const withDrzaveOporezivanja = (values: TaxResidencyInformation): TaxResidencyInformation => {
    const porezniObveznikDrzaveKojaNijeSAD =
      values.porezniObveznikDrzaveKojaNijeSAD ?? (values.drzaveOporezivanja?.length ?? 0) > 0;

    return {
        ...values,
        porezniObveznikDrzaveKojaNijeSAD,
        drzaveOporezivanja: porezniObveznikDrzaveKojaNijeSAD
          ? (values.drzaveOporezivanja?.length ? values.drzaveOporezivanja : [EMPTY_DRZAVA_OPOREZIVANJA])
          : [],
    };
};

function mapTaxResidencyCountryInformationToDrzavaOporezivanja(input: TaxResidencyCountryInformation): DrzavaOporezivanja {
    return {
        drzava: input.country.oznaka,
        porezniBroj: input.taxNumber ? input.taxNumber : '-'
    }
}

function mapTaxResidencyInformationToCreateZahtjevPoreznaRezidentnostRequest(input: TaxResidencyInformation, hasUsCitizenship: boolean): CreateZahtjevPoreznaRezidentnostRequest {
    return {
        imaDodatnoAmerickoDrzavljanstvo: hasUsCitizenship ? false : (input.imaDodatnoAmerickoDrzavljanstvo ?? false),
        zelenaKarta: input.zelenaKarta ?? false,
        porezniObveznikSAD: input.porezniObveznikSAD ?? false,
        posjedujePorezniBrojSAD: input.posjedujePorezniBrojSAD ?? false,
        porezniBrojSAD: toNullOrString(input.porezniBrojSAD),
        drzaveOporezivanja: input.porezniObveznikDrzaveKojaNijeSAD && input.drzaveOporezivanja.length > 0
          ? input.drzaveOporezivanja.map(drzavaOporezivanja => mapTaxResidencyCountryInformationToDrzavaOporezivanja(drzavaOporezivanja))
          : null,
    }
}

const TaxResidencyPage = () => {
    const { t } = useTranslation();
    const { taxResidency, taxResidencyCallback, personalInformation, getZahtjevId, restoreContext, getStatus } = useContext(ZahtjevContext);
    const { isPending: isLoading, mutate: updateTaxResidencyMutation } = useUpdateTaxResidency(getZahtjevId());
    const currentStatus = getStatus();
    const pageStatus = TAX_RESIDENCY_INFORMATION_STATUS;
    const navigate = useNavigate();
    const { notify } = useNotification();
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const initialTaxResidencyInformationFormValues = withDrzaveOporezivanja(
      Object.keys(taxResidency).length !== 0
        ? taxResidency
        : INITIAL_TAX_RESIDENCY_INFORMATION_FORMIK_VALUES
    );
    const hasUsCitizenship =
      personalInformation?.drzavljanstvo?.oznaka === SAD_OZNAKA ||
      (!!personalInformation?.imaDrugoDrzavljanstvo &&
        personalInformation?.drzavljanstvoDrugo?.oznaka === SAD_OZNAKA);

    const handleSubmit = (values: TaxResidencyInformation, setFieldError: (field: string, message: (string | undefined)) => void) => {
        const isFormValid = validateForm(values, setFieldError);

        if (isFormValid) {
            const request = mapTaxResidencyInformationToCreateZahtjevPoreznaRezidentnostRequest(values, hasUsCitizenship);

            updateTaxResidencyMutation(request, {
                onSuccess: () => {
                    taxResidencyCallback(values);
                    navigate("/political-exposure");
                },
                onError: () => {
                    notify(ERROR_MESSAGE_NOTIFICATION, "error");
                }
            });
        } else {
            scrollToFirstError();
        }
    };

    const handleReturn = (values: TaxResidencyInformation, dirty: boolean) => {
        if (dirty) taxResidencyCallback(values);
        navigate('/financial-information', { state: { isBack: true } });
    }

    const handleValidation = (setFieldError: (field: string, message: (string | undefined)) => void) =>
      (value: any, field?: string) => {
          if (field?.includes("country")) {
              return validateField(field!, (value as Country)?.drzava, setFieldError)
          }
          return validateField(field!, value, setFieldError)
      };

    const handleSetFieldValue = (
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
      setFieldError: (field: string, message: string | undefined) => void = () => {
      },
    ) => (value: any, field?: string) => {
        if (field === 'posjedujePorezniBrojSAD' && !value) {
            setFieldValue("porezniBrojSAD", "", false);
            setFieldError(`porezniBrojSAD`, undefined);
        }

        if (field === 'porezniObveznikDrzaveKojaNijeSAD') {
            setFieldValue("drzaveOporezivanja", value ? [EMPTY_DRZAVA_OPOREZIVANJA] : [], false);
            setFieldError("drzaveOporezivanja", undefined);
        }

        return setFieldValue(field!, value, false);
    };

    const handleAddDrzavaOporezivanja = (
      drzaveOporezivanja: TaxResidencyCountryInformation[],
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
      ) => {
        drzaveOporezivanja.push(EMPTY_DRZAVA_OPOREZIVANJA);
        setFieldValue(`drzaveOporezivanja`, drzaveOporezivanja, false);
    };

    const handleRemoveDrzavaOporezivanja = (
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
      setFieldError: (field: string, message: string | undefined) => void,
      drzaveOporezivanja: TaxResidencyCountryInformation[],
    ) => (index: number) => {
        setFieldValue("drzaveOporezivanja", drzaveOporezivanja.filter((_, i) => i !== index), false);
        setFieldError(`drzaveOporezivanja[${index}]`, undefined);
    }

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
      <Card isLoading={parseInt(currentStatus) < parseInt(pageStatus)} bodyClassName="md:w-full">
          <Formik
            initialValues={initialTaxResidencyInformationFormValues}
            onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}
          >{({ values, errors, handleSubmit, setFieldValue, setFieldError, dirty }) => (
            <form noValidate onSubmit={handleSubmit} autoComplete="off">
                <ValidateOnMount shouldValidate={Object.keys(taxResidency).length !== 0} validate={validateForm} />
                <div className="space-y-5 flex flex-col">
                    <div className="!mb-5">
                        <div className="text-lg font-semibold text-gray-700">
                            <span className="inline align-middle">
                                {t("tax-residency-page.description")}
                                <span className="inline-flex items-center translate-y-[3px]">
                                    <Tooltip text={t("tax-residency-page.description-tooltip")}/>
                                </span>
                            </span>
                        </div>
                    </div>

                    {!hasUsCitizenship &&
                        <Switch
                          name="imaDodatnoAmerickoDrzavljanstvo"
                          label={t("tax-residency-page.dual-us-citizenship-field.label")}
                          defaultValue={values.imaDodatnoAmerickoDrzavljanstvo}
                          handleValueChange={handleSetFieldValue(setFieldValue)}
                        />
                    }

                    <Switch
                      name="zelenaKarta"
                      label={t("tax-residency-page.american-green-card-field.label")}
                      defaultValue={values.zelenaKarta}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                    />

                    <Switch
                      name="porezniObveznikSAD"
                      label={t("tax-residency-page.american-tax-payer-field.label")}
                      defaultValue={values.porezniObveznikSAD}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                    />

                    <Switch
                      name="posjedujePorezniBrojSAD"
                      label={t("tax-residency-page.american-tax-number-ownership-field.label")}
                      defaultValue={values.posjedujePorezniBrojSAD}
                      handleValueChange={handleSetFieldValue(setFieldValue, setFieldError)}
                    />

                    {values.posjedujePorezniBrojSAD &&
                        <InputField
                            className="md:w-2/3"
                            name="porezniBrojSAD"
                            label={t("tax-residency-page.american-tax-number-field.label")}
                            maxLength={9}
                            inputValue={values.porezniBrojSAD}
                            error={t(errors?.porezniBrojSAD as string)}
                            handleValueChange={handleSetFieldValue(setFieldValue)}
                            handleValidation={handleValidation(setFieldError)}
                        />
                    }

                    <Switch
                      name="porezniObveznikDrzaveKojaNijeSAD"
                      label={t("tax-residency-page.non-us-tax-payer-field.label")}
                      defaultValue={values.porezniObveznikDrzaveKojaNijeSAD}
                      handleValueChange={handleSetFieldValue(setFieldValue, setFieldError)}
                    />

                    {values.porezniObveznikDrzaveKojaNijeSAD &&
                      <>
                          {values.drzaveOporezivanja.map((drzavaOporezivanja, index) =>
                            <TaxResidencyCountryInput
                              key={index}
                              index={index}
                              value={drzavaOporezivanja}
                              error={errors.drzaveOporezivanja ? (errors.drzaveOporezivanja[index] as FormikErrors<TaxResidencyCountryInformation>) : undefined}
                              handleValueChange={handleSetFieldValue(setFieldValue)}
                              handleValidation={handleValidation(setFieldError)}
                              handleRemoval={handleRemoveDrzavaOporezivanja(setFieldValue, setFieldError, values.drzaveOporezivanja)}
                              excludedCountries={values?.drzaveOporezivanja.flatMap(drzavaOporezivanja => drzavaOporezivanja?.country?.oznaka).filter(value => !!value)}
                              isRemovalDisabled={values?.drzaveOporezivanja?.length === 1}
                            />
                          )}
                          {(values.drzaveOporezivanja.length < DRZAVE_OPOREZIVANJA_MAX_LENGTH) && (
                            <Grid container>
                                <Grid size={isMobile ? 12 : 4}>
                                    <Button
                                      className={isMobile ? "w-full" : "w-fit"}
                                      onClick={() => handleAddDrzavaOporezivanja(values.drzaveOporezivanja, setFieldValue)}
                                      type="button"
                                      text={t("tax-residency-page.button-add-country")}
                                      img={<AddIcon fontSize="medium"/>}
                                      disabled={!isDrzaveOporezivanjaValid(values.drzaveOporezivanja)}
                                    />
                                </Grid>
                            </Grid>
                          )}
                      </>
                    }
                </div>

                <div className="mt-10 text-center md:text-left">
                    <Button
                      onClick={() => handleReturn(values, dirty)}
                      type="button"
                      text={t("common.button-previous")}
                      variant="secondary"
                      disabled={false}
                    />
                    <Button
                      type="submit"
                      text={t("common.button-next")}
                      disabled={isLoading}
                    />
                </div>
            </form>
          )}
          </Formik>
      </Card>
    );
};

export default TaxResidencyPage;
